import {
  Button,
  Flex,
  Heading,
  VStack,
  Grid,
  GridItem,
  Box,
} from "@chakra-ui/react";
import { Form, Formik } from "formik";
import React, { useEffect, useRef, useState } from "react";
import Swal from "sweetalert2";
import { useHistory } from "react-router-dom";
import LoadingSpinner from "../../components/LoadingSpinner";
import { FocusError } from "../../components/FocusError";
import * as Yup from "yup";
import pt from "date-fns/locale/pt";
import { registerLocale, setDefaultLocale } from "react-datepicker";

import CriancaService from "../../../main/services/CriancaService";
import {
  format,
  intervalToDuration,
  isAfter,
  isEqual,
  isValid,
  parse,
} from "date-fns";
import { CustomSelectWField } from "../../components/CustomSelectWField";
import { CustomDatepickerWField } from "../../components/CustomDatepickerWField";
import VisitasService from "../../../main/services/VisitasService";
import SemRegistros from "../../components/SemRegistros";
import { useAuthProvider } from "../../../Context/AuthProvider";
import { visitasFunc, criancaFunc } from "../../../helpers/Funcionalidades";

const schema = Yup.object().shape({
  crianca: Yup.object().nullable().required("Este campo é obrigatório!"),
  visitante: Yup.object().nullable().required("Este campo é obrigatório!"),
  data: Yup.string()
    .required("Este campo é obrigatório!")
    .nullable()
    .test("validDate", "Data inválida!", (value) => {
      if (!!value) {
        if (value.length === 10) {
          return isValid(parse(value, "dd/MM/yyyy", new Date()));
        } else {
          return false;
        }
      } else {
        return true;
      }
    })
    .test(
      "dataNascimentoAfterToday",
      "A data informada não pode ser uma data futura!",
      (value) => {
        if (!value) {
          return true;
        } else {
          return !isAfter(parse(value, "dd/MM/yyyy", new Date()), new Date());
        }
      }
    )
    .test(
      "dataNascimentoGreaterThan100",
      "A idade informada não pode ser superior a 100 anos!",
      (value) => {
        if (!value) {
          return true;
        } else {
          try {
            const parsedDate = parse(value, "dd/MM/yyyy", new Date(), {
              awareOfUnicodeTokens: true,
            });

            if (!isValid(parsedDate)) {
              return false;
            }
            const idade = intervalToDuration({
              start: parsedDate,
              end: new Date(),
            }).years;

            return idade <= 100;
          } catch (error) {
            return false;
          }
        }
      }
    )
    .test(
      "dataVisitaAfterNascimento",
      "Data da visita informada menor que a data de nascimento do acolhido.",
      function (value) {
        const { data, crianca } = this.parent;

        if (!!data && !!crianca && crianca.dataNascimento) {
          const dataNascimento = new Date(crianca.dataNascimento);

          const partesData = value.split("/");
          const dataVisita = new Date(
            partesData[2],
            partesData[1] - 1,
            partesData[0]
          );

          return (
            isAfter(dataVisita, dataNascimento) ||
            isEqual(dataVisita, dataNascimento)
          );
        } else {
          return false;
        }
      }
    ),
});

const EditarVisitas = (props) => {
  document.title = "MCA - Visitas - Editar";
  let history = useHistory();
  const { roleKeycloack, user } = useAuthProvider();

  const _IsMounted = useRef(false);

  const [criancaId, setCriancaId] = useState("");
  const [loading, setLoading] = useState(true);
  const [selectedCrianca, setSelectedCrianca] = useState(null);
  const [relations, setRelations] = useState({ options: [], isLoading: false });

  const [initialValues, setInitialValues] = useState({
    crianca: "",
    visitante: "",
    data: "",
    idTipoReferencia: "",
  });

  useEffect(() => {
    setDefaultLocale("pt-br");
    registerLocale("pt-br", pt);
  }, []);

  useEffect(() => {
    const notFoud = () => {
      Swal.fire({
        title: "Atenção!",
        text: "Registro não encontrado",
        icon: "warning",
        confirmButtonText: "Fechar",
        confirmButtonColor: "#169499",
      }).then(() => {
        history.push("/visitas/lista");
      });
    };

    const fetchData = () => {
      VisitasService.getVisita(props.match.params.id)
        .then((res) => {
          const {
            pessoaFisicaVisitanteResponse,
            dataVisita,
            servicoAcolhimentoResponse,
            idCrianca,
          } = res?.data;

          const custom_date = new Date(dataVisita).toLocaleDateString("pt-BR");
          setCriancaId(idCrianca);

          CriancaService.getCrianca(idCrianca)
            .then((res) => {
              const formattedOptions = {
                dataNascimento: res.data.dataNascimento,
                label:
                  res.data.nome +
                  `${res.data.nomeSocial ? ` / ${res?.data?.nomeSocial}` : ""}`,
                value: res.data.id,
                idSeac: servicoAcolhimentoResponse.id
                  ? servicoAcolhimentoResponse.id
                  : null,
                servico: res.data.servicoAcolhimento,
              };

              setSelectedCrianca(formattedOptions);

              return formattedOptions;
            })
            .then((formattedOptions) => {
              setInitialValues({
                crianca: formattedOptions,
                visitante: {
                  nome: pessoaFisicaVisitanteResponse.nome,
                  id: pessoaFisicaVisitanteResponse.id,
                },
                data: custom_date,
                idTipoReferencia: formattedOptions.servico,
              });

              setLoading(false);
            })
            .catch((err) => {
              notFoud();
            });
        })
        .catch((err) => {
          notFoud();
        });
    };

    _IsMounted.current = true;
    fetchData();
    return () => {
      _IsMounted.current = false;
    };
  }, [props.match.params.id, history]);

  const handleGetRelacionamento = (props) => {
    let dataFormatada;
    setRelations({ ...[], isLoading: true });

    if (
      props.values.data &&
      isValid(parse(props.values.data, "dd/MM/yyyy", new Date()))
    ) {
      dataFormatada = format(
        parse(props.values.data, "dd/MM/yyyy", new Date()),
        "yyyy-MM-dd"
      );
    }

    VisitasService.relacionamentosVisita(
      criancaId,
      dataFormatada ? dataFormatada : "",
      selectedCrianca?.idSeac
    ).then((res) => {
      setRelations({ options: res.data, isLoading: false });
    });
  };

  return !loading ? (
    <Flex
      pt={"20px"}
      px={["5px", "30px"]}
      h="100%"
      flexDir="column"
      w="100%"
      maxW="2000px"
      overflow="auto"
      pb={["10px", "20px"]}
    >
      <Flex flexDir="row" justifyContent="space-between">
        <Heading fontSize="large">Editar Visita</Heading>

        <Button
          colorScheme={"teal"}
          px={16}
          onClick={() => {
            history.push("/visitas/lista");
          }}
        >
          Voltar
        </Button>
      </Flex>
      {roleKeycloack.includes(visitasFunc.visitaEditar) ||
      (roleKeycloack.includes(criancaFunc.visualizarTudoAcolhimento) &&
        user.orgaoExterno.includes(
          selectedCrianca?.servico?.idPessoaJuridica
        )) ? (
        <Formik
          validationSchema={schema}
          initialValues={initialValues}
          validateOnChange={false}
          validateOnBlur={false}
          onSubmit={(values, actions) => {
            const { visitante, data } = values;

            const splitDate = data.split("/");
            const year = parseInt(splitDate[2]);
            const month = parseInt(splitDate[1]) - 1;
            const day = parseInt(splitDate[0]);
            const now = new Date();

            const formattedDate = new Date(
              year,
              month,
              day,
              now.getHours(),
              now.getMinutes(),
              now.getSeconds()
            );
            const formattedDateString = format(
              formattedDate,
              "yyyy-MM-dd'T'HH:mm:ss.SSSxxx"
            );

            const valuesToSend = {
              dataVisita: data ? formattedDateString : "",
              idPessoaFisicaVisitante: visitante ? visitante.id : "",
            };

            Swal.fire({
              title:
                "Atenção: Ao editar, as informações anteriores serão perdidas!",
              text: "Deseja prosseguir?",
              icon: "warning",
              showCancelButton: true,
              confirmButtonColor: "#169499",
              cancelButtonColor: "#169499",
              confirmButtonText: "Salvar",
              cancelButtonText: "Cancelar",
            }).then((result) => {
              if (result.isConfirmed) {
                VisitasService.putVisitas(props.match.params.id, valuesToSend)
                  .then((res) => {
                    Swal.fire({
                      title: "Sucesso!",
                      text: "Registro atualizado com sucesso!",
                      icon: "success",
                      confirmButtonText: "Continuar",
                      confirmButtonColor: "#169499",
                    }).then(function () {
                      actions.setSubmitting(false);
                      history.push("/visitas/" + res.data.idVisita);
                    });
                    actions.setSubmitting(false);
                  })
                  .catch((error) => {
                    Swal.fire({
                      title: "Atenção!",
                      text: error.response.data.message
                        ? error.response.data.message
                        : "",
                      icon: "warning",
                      confirmButtonText: "Fechar",
                      confirmButtonColor: "#169499",
                    });
                    actions.setFieldError(
                      error.response && error.response.data.field,
                      error.response && error.response.data.message
                    );
                    actions.setSubmitting(false);
                  });
              } else {
                history.push("/visitas/" + props.match.params.id);
                actions.setSubmitting(false);
              }
            });

            actions.setSubmitting(false);
          }}
        >
          {(props) => (
            <Flex flexDir="column" mt={4}>
              <Form mt>
                <FocusError />

                <Box bgColor="#FFF" borderRadius="16px" p="20px">
                  <Box w={{ md: "60%" }}>
                    <Grid mt={5} templateColumns="repeat(5, 1fr) 1fr" gap={2}>
                      <GridItem colSpan={{ base: 3, lg: 4, xl: 5 }}></GridItem>
                    </Grid>

                    <Flex>
                      <CustomSelectWField
                        fieldName="idTipoReferencia"
                        label="Serviço de Acolhimento*"
                        placeholder="Selecione o Serviço de Acolhimento"
                        getOptionLabel={(option) => option.nomeFantasia}
                        getOptionValue={(option) => option.id}
                        isDisabled={true}
                      />
                    </Flex>

                    <Flex mt={5}>
                      <CustomSelectWField
                        fieldName="crianca"
                        label="Criança / Adolescente*"
                        placeholder="Selecione a criança/adolescente"
                        getOptionLabel={(option) => option.label}
                        getOptionValue={(option) => option.value}
                        options={selectedCrianca}
                        defaultValue={selectedCrianca}
                        isDisabled={true}
                        onChange={(value) => {
                          props.setFieldValue("crianca", value);
                        }}
                      />
                    </Flex>

                    <Grid mt={5} templateColumns="repeat(5, 1fr) 1fr" gap={2}>
                      <GridItem colSpan={{ base: 4, lg: 3, xl: 2 }}>
                        <VStack alignItems="flex-start">
                          <CustomDatepickerWField
                            fieldName="data"
                            label="Data*"
                            placeholder="DD/MM/AAAA"
                            maxDate={new Date()}
                            clear={() => props.setFieldValue("visitante", "")}
                          />
                        </VStack>
                      </GridItem>
                      <GridItem colSpan={{ base: 6, xl: 4 }}>
                        <CustomSelectWField
                          fieldName="visitante"
                          label="Visitante*"
                          placeholder="Selecione o visitante"
                          getOptionLabel={(option) => option.nome}
                          getOptionValue={(option) => option.id}
                          isLoading={relations.isLoading}
                          options={relations.options}
                          onChange={(value) => {
                            props.setFieldValue("visitante", value);
                          }}
                          onFocus={() => handleGetRelacionamento(props)}
                        />
                      </GridItem>
                    </Grid>
                  </Box>
                </Box>
                <Flex mt={4} justifyContent="flex-end">
                  <Button
                    colorScheme="teal"
                    px={16}
                    isLoading={props.isSubmitting}
                    type="submit"
                    translate="no"
                  >
                    Salvar
                  </Button>
                </Flex>
              </Form>
            </Flex>
          )}
        </Formik>
      ) : (
        <Box mt={4}>
          <SemRegistros buttonVisible={true} half />
        </Box>
      )}
    </Flex>
  ) : (
    <Flex w="100%" mt="25%" justifyContent="center">
      <LoadingSpinner />
    </Flex>
  );
};
export default EditarVisitas;
